import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartTimeseriesData } from './types';

const DEFAULT_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

interface LineChartProps {
  data: ChartTimeseriesData;
  colors?: string[];
}

/**
 * Multi-series line chart for timeseries data. Legend is hidden when there is
 * only one series. Fills its parent container via `ResponsiveContainer`.
 *
 * @example
 * <LineChart
 *   data={{
 *     labels: ['Jan', 'Feb', 'Mar'],
 *     series: [{ name: 'Completed', values: [12, 18, 15] }],
 *   }}
 *   colors={['#6366f1']}
 * />
 */
export function LineChart({ data, colors }: LineChartProps) {
  const palette = colors?.length ? colors : DEFAULT_COLORS;

  const chartData = data.labels.map((label, i) => {
    const row: Record<string, unknown> = { name: label };
    data.series.forEach((s) => {
      row[s.name] = s.values[i] ?? 0;
    });
    return row;
  });

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsLineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.1} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
        <Tooltip
          formatter={(value) =>
            typeof value === 'number' ? value.toLocaleString() : String(value ?? '')
          }
        />
        {data.series.length > 1 && (
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
        )}
        {data.series.map((s, index) => (
          <Line
            key={s.name}
            type="monotone"
            dataKey={s.name}
            stroke={s.color ?? palette[index % palette.length]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}
